import { breadcrumbJsonLd } from "@amtana/browser";
import type { defineBrowserModule, ScreenDeclaration } from "@amtana/browser";
import { pathWithLanguage } from "@amtana/i18n";
import { shippedLanguage } from "@amtana/i18n/catalog";
import type { Phrase, SupportedLanguage } from "@amtana/i18n/catalog";
import { POSTS } from "virtual:blog-posts";
import type { Post, PostFile } from "virtual:blog-posts";

import { BLOG_PATH, BLOG_WORDS, feedLink } from "./features/blog/model/blog-links.ts";
import { postPath } from "./features/blog/model/blog-posts.ts";
import { SITE_URL } from "./model/site-url.ts";
import { postJsonLd } from "./post-head.web.ts";

// The blog's screens (ADR-029 points 2 and 3). The index is a literal screen `screen-seo` reads. A
// post's screen comes from the manifest, so the frontmatter schema and `pnpm site:audit` hold its
// head instead. A post exists in the languages it has a published file for.

const phrase = (read: (language: SupportedLanguage) => string): Phrase => ({
  kind: "phrase",
  read: (language) => read(shippedLanguage(language)),
});

function blogCrumb(language: SupportedLanguage) {
  return { name: BLOG_WORDS[language].eyebrow, path: pathWithLanguage(BLOG_PATH, language) };
}

function postScreen(post: Post): ScreenDeclaration {
  const languages = (["en", "es"] as const).filter((language) => post.languages[language]);
  // The route table never resolves this screen in a language it lacks; the fallback is for types.
  const fileIn = (language: SupportedLanguage): PostFile =>
    (post.languages[language] ?? post.languages[languages[0] ?? "en"]) as PostFile;
  const path = postPath(post.slug);
  const image = fileIn("en").image ?? fileIn("es").image;
  return {
    path,
    languages,
    title: phrase((language) => fileIn(language).seoTitle),
    seo: {
      description: phrase((language) => fileIn(language).description),
      keyphrase: phrase((language) => fileIn(language).keyphrase ?? ""),
      contentType: "article",
      type: "article",
      ...(image ? { image } : {}),
      alternateLinks: (language) => {
        const shipped = shippedLanguage(language);
        return [
          feedLink(shipped),
          {
            type: "text/markdown",
            title: BLOG_WORDS[shipped].twinTitle,
            href: `${pathWithLanguage(path, shipped)}.md`,
          },
        ];
      },
      jsonLd: (language) => {
        const shipped = shippedLanguage(language);
        return postJsonLd({
          file: fileIn(shipped),
          language: shipped,
          address: pathWithLanguage(path, shipped),
          blog: blogCrumb(shipped),
        });
      },
      sources: languages.map((language) => fileIn(language).file),
    },
    load: async () => {
      const { postScreenFor } = await import("./ui/sections/post-screen.tsx");
      return { default: await postScreenFor(post) };
    },
  };
}

const postScreens = Object.fromEntries(POSTS.map((post) => [post.slug, postScreen(post)]));

/** The blog's screens (ADR-034 point 1). The index stays a literal `.withScreens({ ... })`, since the
 * `screen-seo` policy reads this file as text. */
export function withBlogScreens(site: ReturnType<typeof defineBrowserModule>) {
  return site
    .withScreens({
      blog: {
        path: "/blog",
        title: phrase((language) => BLOG_WORDS[language].seoTitle),
        seo: {
          description: phrase((language) => BLOG_WORDS[language].description),
          keyphrase: phrase((language) => BLOG_WORDS[language].keyphrase),
          contentType: "landing",
          alternateLinks: (language) => [feedLink(shippedLanguage(language))],
          jsonLd: (language) => [
            breadcrumbJsonLd({ siteUrl: SITE_URL, items: [blogCrumb(shippedLanguage(language))] }),
          ],
          sources: [
            "modules/site/browser/src/features/blog/model/blog-words-en.ts",
            "modules/site/browser/src/features/blog/model/blog-words-es.ts",
          ],
        },
        load: () => import("./ui/sections/blog-screen.tsx"),
      },
    })
    .withScreens(postScreens);
}
